import type {
  SolProposal,
  SolAnswerResult,
  SolCandidateEvidence,
  SolConflictBasis,
  SolGrounding,
  SolMemoryCard,
  SolProjectRef,
  SolSourceCard,
  SolSpeechAuthorization,
} from '../../data/sol/sol-contract';
import { solSourceKey } from '../../data/sol/sol-contract';
import {
  CLOSE_MATCHES_TEXT,
  CONFLICT_ANSWER_NOTE,
  CONTRACT_FAILURE_TEXT,
  describeNoEvidence,
  noEvidenceScopeNoteApplies,
  OVERSIZED_EVIDENCE_TEXT,
  clarificationText,
  describeCloseMatchCount,
  describeCount,
  describePhotoCount,
  describeEvidenceBound,
  describeProjectCount,
  describeProjectListBound,
  describeFoundButUnanswered,
  describeScope,
  failureWording,
  memoryClarificationText,
  memoryClearPromptText,
  MEMORY_FORGOTTEN_TEXT,
  memoryRefusedText,
  memorySavedText,
  projectLabel,
  unsupportedText,
} from './sol-wording';

/**
 * ONE PURE FUNCTION FROM A `sol-ask` RESULT TO WHAT A SCREEN DRAWS.
 * M9 / S09-04.
 *
 * **CONTRACT INTERPRETATION LIVES HERE AND NOWHERE ELSE.** Scattering `if
 * (result.kind === …)` through JSX is how one branch quietly stops being
 * handled: the compiler cannot see a missing case in a chain of ternaries, and
 * the symptom is a blank card rather than an error. The switch below is
 * exhaustive and closed by a `never` assignment, so **a new result variant is a
 * type error at build time, not an empty box on a phone.**
 *
 * **IT IS PURE AND SYNCHRONOUS.** No fetch, no clock, no router, no React. That
 * is what lets every result variant be asserted as a value.
 *
 * **IT ADDS NO FACTS.** Counts, scope, broadening, claim text, claim grounding
 * and source cards are the server's; this function reorganises them and wraps
 * them in Crewlink's own deterministic wording. **It never resolves a conflict,
 * never re-derives an answer state, never re-orders claims, and never invents a
 * source card.**
 */

/** How prominent, and in what voice, the answer body should read. */
export type SolPresentationTone = 'answer' | 'question' | 'nothing' | 'failure';

export interface SolPresentedClaim {
  readonly id: string;
  /** The model's own words, unaltered. **The only such string on the screen.** */
  readonly text: string;
  readonly grounding: SolGrounding;
  /** Present only on a conflicting claim — mirroring the server's union. */
  readonly conflictBasis: SolConflictBasis | null;
  /**
   * The cards this claim cites, resolved by `{domain, id}` **only**.
   * Never by array index, order, position or Capture number.
   */
  readonly cards: readonly SolSourceCard[];
}

/**
 * What a memory outcome puts on the screen beside Crewlink's sentence (M11 /
 * D-040): the memory itself (so Forget / Undo can name it), the candidates of
 * a clarification, or the clear-all confirmation request.
 */
export type SolMemoryPresentation =
  | { readonly kind: 'saved'; readonly memory: SolMemoryCard }
  | { readonly kind: 'forgotten'; readonly memory: SolMemoryCard }
  | { readonly kind: 'candidates'; readonly candidates: readonly SolMemoryCard[] }
  | { readonly kind: 'clear_requested'; readonly activeCount: number };

export interface SolPresentation {
  /**
   * Mirrors `result.kind`, plus `contract_failure` for impossible responses
   * and `restored` for a stored Sol message drawn back after a restart (M11).
   */
  readonly kind: SolAnswerResult['kind'] | 'contract_failure' | 'restored';
  readonly tone: SolPresentationTone;
  /** The main sentence. Crewlink's words for everything except claims. */
  readonly body: string;
  /** Claims, in SERVER ORDER. Empty for every non-grounded result. */
  readonly claims: readonly SolPresentedClaim[];
  /** Cited cards, in server order, for the answer as a whole. */
  readonly cards: readonly SolSourceCard[];
  /** Close-match candidates. **Never presented as findings.** */
  readonly candidates: readonly SolCandidateEvidence[];
  /** Short deterministic facts: scope, broadening, bounding, conflict. */
  readonly notes: readonly string[];
  /** Candidate Project names, as INFORMATION. See `sol-choices` note below. */
  readonly candidateProjectNames: readonly string[];
  readonly retryable: boolean;
  /** Non-null only for an eligible grounded answer on a speech-enabled server. */
  readonly speech: SolSpeechAuthorization | null;
  /**
   * **A PROPOSAL, NOT A CHANGE.** Non-null only when the server bound exactly
   * one Capture and issued a signed, short-lived ticket for it. Nothing has
   * been written; nothing will be until Brandon confirms. Every other result —
   * and every grounded answer without one — carries `null`, which renders as
   * no card at all.
   */
  readonly proposal: SolProposal | null;
  /** A memory outcome's extra (M11 / D-040), or null for every other result. */
  readonly memory: SolMemoryPresentation | null;
}

function names(projects: readonly SolProjectRef[]): readonly string[] {
  return projects.map((project) => projectLabel(project.name));
}

function empty(
  kind: SolPresentation['kind'],
  tone: SolPresentationTone,
  body: string,
  notes: readonly string[] = [],
): SolPresentation {
  return {
    kind,
    tone,
    body,
    claims: [],
    cards: [],
    candidates: [],
    notes,
    candidateProjectNames: [],
    retryable: false,
    speech: null,
    proposal: null,
    memory: null,
  };
}

/**
 * A STORED Sol message, drawn back after a restart or when an earlier
 * conversation is reopened (M11 / D-041). **Crewlink's own stored words, not
 * a fresh answer**: no claims, no cards, no proposal, no speech — the cards
 * and proposals of the original turn are not re-issued, because re-offering a
 * write or a citation from a stored sentence would be authority from memory.
 */
export function presentRestoredSolMessage(body: string): SolPresentation {
  return empty('restored', 'answer', body);
}

/**
 * Join claims to cards by `{domain, id}`.
 *
 * **A MISSING CARD IS A CONTRACT FAILURE, NOT A GAP TO FILL.** The server builds
 * cards from the evidence it actually sent and validates every citation against
 * that same set, so a claim citing a card that is not in the response means one
 * of those two guarantees did not hold. Rendering the claim without its card
 * would show an uncited sentence dressed as a grounded one; reconstructing the
 * card from the claim would be the browser inventing evidence. Neither is
 * acceptable, so the whole answer is withheld.
 */
function joinClaims(
  answer: Extract<SolAnswerResult, { kind: 'grounded_answer' }>,
): readonly SolPresentedClaim[] | null {
  const byKey = new Map(answer.sourceCards.map((card) => [solSourceKey(card.source), card]));

  const presented: SolPresentedClaim[] = [];
  for (const claim of answer.claims) {
    const cards: SolSourceCard[] = [];
    for (const source of claim.sources) {
      const card = byKey.get(solSourceKey(source));
      if (card === undefined) return null;
      cards.push(card);
    }
    presented.push({
      id: claim.id,
      text: claim.text,
      grounding: claim.grounding,
      // Read off the union, never off an optional field: a supported claim has
      // no `conflictBasis` to read, and this is the one place that is decided.
      conflictBasis: claim.grounding === 'conflicting' ? claim.conflictBasis : null,
      cards,
    });
  }
  return presented;
}

export function presentSolResult(
  result: SolAnswerResult,
  currentProjectId: string | null,
): SolPresentation {
  switch (result.kind) {
    case 'needs_clarification': {
      return {
        ...empty(
          'needs_clarification',
          'question',
          clarificationText(result.reason, result.phrase),
        ),
        // **INFORMATION, NOT CONTROLS.** Naming the Projects that collide tells
        // him what to type. A button here would re-send an identical request —
        // the planner resolves an explicit Project name before it ever consults
        // the Project on screen — and get the identical question back. A control
        // that cannot change the outcome is the fake-scope-selector mistake with
        // a different label.
        candidateProjectNames: names(result.candidateProjects),
      };
    }

    case 'unsupported':
      return empty('unsupported', 'question', unsupportedText(result.reason, result.phrase));

    case 'no_evidence':
      // **WORDED AS THE DOMAIN THE SERVER EXECUTED** — a report empty result is
      // about reports, a photo one about photos. The domain is data on the
      // result, derived from the capability; nothing here reads the question.
      return empty(
        'no_evidence',
        'nothing',
        describeNoEvidence(result.domain, result.scope, currentProjectId),
        noEvidenceScopeNoteApplies(result.domain)
          ? [describeScope(result.scope, currentProjectId)]
          : [],
      );

    case 'close_matches':
      return {
        ...empty('close_matches', 'nothing', CLOSE_MATCHES_TEXT, [
          describeScope(result.scope, currentProjectId),
          // **A COUNT OF GUESSES, SAID AS ONE.** `describeCount` belongs to the
          // exact-count path and must never appear here: it phrases
          // approximation in the vocabulary of arithmetic.
          describeCloseMatchCount(result.totalMatchCount),
        ]),
        candidates: result.candidates,
      };

    case 'count':
      return empty(
        'count',
        'answer',
        result.domain === 'project'
          ? describeProjectCount(result.totalMatchCount)
          : result.domain === 'photo'
            ? describePhotoCount(result.totalMatchCount)
            : describeCount(result.totalMatchCount),
        // A Project count is not scoped to a Project; the scope sentence is a
        // Capture-search sentence and would be false under it.
        result.domain === 'project' ? [] : [describeScope(result.scope, currentProjectId)],
      );

    case 'oversized_evidence':
      return empty('oversized_evidence', 'nothing', OVERSIZED_EVIDENCE_TEXT, [
        describeScope(result.scope, currentProjectId),
      ]);

    case 'grounded_answer': {
      const claims = joinClaims(result);
      // A citation the response could not carry. Withheld whole — the same
      // posture the server's validator takes, for the same reason.
      if (claims === null) return contractFailure();

      const notes: string[] = [describeScope(result.scope, currentProjectId)];
      // **BOTH HALVES OF A COMPOUND READ SURVIVE TO THE SCREEN** (M11 / I-067).
      // He asked how many AND to see them; the cards are below, and the number
      // is stated here as CREWLINK's own sentence — never left to whether the
      // model's wording happened to include it. It leads the notes because it
      // is the first half of what he asked.
      if (typeof result.countedTotal === 'number') {
        notes.unshift(describeCount(result.countedTotal));
      }
      if (result.answerState === 'conflicting') notes.push(CONFLICT_ANSWER_NOTE);
      // **A CAPTURE RETRIEVAL NOTE NEEDS CAPTURES.** The count guard is not
      // decoration: a Project answer carries no Capture evidence, and this line
      // once rendered as "Sol answered from the 0 closest of 0 matching
      // Captures." Worse, a Project answer that fell back to a Capture reply
      // showed "the 83 closest of 2,148 matching Captures" — a true sentence
      // about a search he never asked for, under an answer about his Projects.
      if (result.evidenceBounded && result.evidenceSentCount > 0) {
        notes.push(describeEvidenceBound(result.evidenceSentCount, result.totalMatchCount));
      }
      // **CREWLINK SAYS THE LIST WAS CUT SHORT — NOT THE MODEL.** The answer
      // instructions forbid the model from describing the search at all, so a
      // list of 25 Projects out of 60 would otherwise read as the whole set.
      if (result.projectsBounded) {
        notes.push(describeProjectListBound(result.projectsSentCount));
      }

      return {
        kind: 'grounded_answer',
        tone: 'answer',
        // **UNKNOWN IS AN ANSWER, AND IT IS CREWLINK'S SENTENCE** — the model
        // returns no prose for it at all (J-15). It is never downgraded into
        // `no_evidence`: relevant records existed and simply did not establish
        // the fact, which is a different thing from finding nothing.
        body: result.answerState === 'unknown' ? (result.unknownText ?? '') : '',
        claims,
        cards: result.sourceCards,
        candidates: [],
        notes,
        candidateProjectNames: [],
        retryable: false,
        speech: result.speech ?? null,
        // Passed through UNINTERPRETED. This function decides nothing about
        // whether an action is safe — the server already bound it, and the
        // screen still has to ask.
        proposal: result.proposal ?? null,
        memory: null,
      };
    }

    // ---- MEMORY OUTCOMES (M11 / D-040) — Crewlink's sentences, never the model's ----
    case 'memory_saved':
      return {
        ...empty('memory_saved', 'answer', memorySavedText(result.replacedContent)),
        memory: { kind: 'saved', memory: result.memory },
      };

    case 'memory_forgotten':
      return {
        ...empty('memory_forgotten', 'answer', MEMORY_FORGOTTEN_TEXT),
        memory: { kind: 'forgotten', memory: result.memory },
      };

    case 'memory_clarification':
      return {
        ...empty(
          'memory_clarification',
          result.candidates.length === 0 ? 'nothing' : 'question',
          memoryClarificationText(result.candidates.length, result.description),
        ),
        memory:
          result.candidates.length === 0
            ? null
            : { kind: 'candidates', candidates: result.candidates },
      };

    case 'memory_clear_requested':
      return {
        ...empty('memory_clear_requested', 'question', memoryClearPromptText(result.activeCount)),
        memory:
          result.activeCount === 0
            ? null
            : { kind: 'clear_requested', activeCount: result.activeCount },
      };

    case 'memory_refused':
      return empty('memory_refused', 'question', memoryRefusedText(result.reason));

    case 'reasoning_failed': {
      const wording = failureWording(result.failure);
      const notes: string[] = [describeScope(result.scope, currentProjectId)];
      // Same rule as the grounded answer: a Capture count belongs only to a
      // failure that was actually about a Capture retrieval. A capability
      // package reports its own totals, which are zero for a Project lookup.
      if (result.totalMatchCount > 0) {
        // **TRUTHFUL, AND DELIBERATELY NOT A BUTTON.** The response carries the
        // COUNT of what matched and not the identities, so "Show these Captures"
        // could not navigate anywhere honest — least of all across Projects.
        notes.unshift(describeFoundButUnanswered(result.totalMatchCount));
      }
      return {
        ...empty('reasoning_failed', 'failure', wording.text, notes),
        retryable: wording.retryable,
      };
    }

    default: {
      // **EVERY VARIANT ABOVE IS HANDLED, AND THE COMPILER ENFORCES IT.** If a
      // future `sol-ask` grows a result this build has never seen — including
      // the internal `reasoning_required`, which must never leave the endpoint —
      // this assignment fails to compile. At runtime it degrades to a safe,
      // non-technical message rather than a blank card.
      const exhaustive: never = result;
      void exhaustive;
      return contractFailure();
    }
  }
}

export function contractFailure(): SolPresentation {
  return empty('contract_failure', 'failure', CONTRACT_FAILURE_TEXT);
}
